import { useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "@thuthi/ui/components/sonner";
import { Bell, BellOff, CircleMinus, ImageMinus, ImagePlus, ListX, Pencil, Send, X } from "@thuthi/ui/icons";
import { Button } from "@thuthi/ui/components/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@thuthi/ui/components/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@thuthi/ui/components/dropdown-menu";
import { Input } from "@thuthi/ui/components/input";
import subscriptionManager from "../app/SubscriptionManager";
import DialogFooter from "./DialogFooter";
import accountApi from "../app/AccountApi";
import session from "../app/Session";
import routes from "./routes";
import { formatShortDateTime, shuffle } from "../app/utils";
import api from "../app/Api";
import { UnauthorizedError } from "../app/errors";
import type { Subscription } from "../app/types";

/**
 * 구독 액션 메뉴. MUI 시절에는 호출측이 anchorEl 상태를 관리하며 `anchor`/`onClose` 로 위치와
 * 열림을 제어했지만, Radix DropdownMenu 는 트리거가 메뉴 내부에 있어야 하므로 메뉴를 여는
 * 요소를 `trigger` prop 으로 받아 `DropdownMenuTrigger asChild` 로 감싼다. 열림 상태는
 * DropdownMenu 가 내부에서 관리한다(비제어).
 *
 * props:
 * - subscription: 대상 구독
 * - trigger: 메뉴를 여는 요소(예: <Button variant="ghost" size="icon">…</Button>)
 * - placement: "left"(기본) | "right" — 원본 PopupMenu 의 horizontal 에 대응 (right → align="end")
 */
interface SubscriptionPopupProps {
  subscription: Subscription;
  trigger: ReactNode;
  placement?: "left" | "right";
}

export const SubscriptionPopup = (props: SubscriptionPopupProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [displayNameDialogOpen, setDisplayNameDialogOpen] = useState(false);
  const { subscription } = props;
  const placement = props.placement ?? "left";

  const handleChangeDisplayName = async () => {
    setDisplayNameDialogOpen(true);
  };

  // 구독 아이콘(사진) — 서버가 가운데 정사각 256px 로 잘라 저장하고, 웹 목록·사이드바와 앱 알림 큰 아이콘에 쓴다
  const iconInputRef = useRef<HTMLInputElement>(null);
  const handleIconFile = async (ev: ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    ev.target.value = ""; // 같은 사진을 다시 골라도 change 가 오게
    if (!file) {
      return;
    }
    const id = toast.loading("아이콘을 올리는 중…");
    try {
      await api.putSubscriptionIcon(subscription.topic, file);
      await accountApi.sync();
      toast.success("아이콘을 바꿨어요", { id });
    } catch (e) {
      console.log(`[SubscriptionPopup] Icon upload failed`, e);
      toast.error(file.size > 10 * 1024 * 1024 ? "사진이 너무 커요(10MB 까지)" : "아이콘을 올리지 못했어요", { id });
    }
  };
  const handleRemoveIcon = async () => {
    try {
      await api.deleteSubscriptionIcon(subscription.topic);
      await accountApi.sync();
      toast.success("아이콘을 지웠어요");
    } catch (e) {
      console.log(`[SubscriptionPopup] Icon delete failed`, e);
      toast.error("아이콘을 지우지 못했어요");
    }
  };

  const handleSendTestMessage = async () => {
    const { topic } = props.subscription;
    const tags = shuffle([
      "grinning",
      "octopus",
      "upside_down_face",
      "palm_tree",
      "maple_leaf",
      "apple",
      "skull",
      "warning",
      "jack_o_lantern",
      "de-server-1",
      "backups",
      "cron-script",
      "script-error",
      "phils-automation",
      "mouse",
      "go-rocks",
      "hi-ben",
    ]).slice(0, Math.round(Math.random() * 4));
    // v2 는 제목·본문이 둘 다 필수다
    const title = shuffle([
      "테스트 메시지",
      "Oh my, another test message?",
      "ntfy is open source, and will always be free. Cool, right?",
      "I don't really like apples",
      "You can attach several photos to one message",
    ])[0];
    const nowSeconds = Math.round(Date.now() / 1000);
    const message = shuffle([
      `Hello friend, this is a test notification from ntfy web. It's ${formatShortDateTime(
        nowSeconds,
        "en-US"
      )} right now. Is that early or late?`,
      `So I heard you like ntfy? If that's true, go to GitHub and star it, or to the Play store and rate it. Thanks! Oh yeah, this is a test notification.`,
      `It's almost like you want to hear what I have to say. I'm not even a machine. I'm just a sentence that Phil typed on a random Thursday.`,
      `Alright then, it's ${formatShortDateTime(
        nowSeconds,
        "en-US"
      )} already. Boy oh boy, where did the time go? I hope you're alright, friend.`,
      `There are nine million bicycles in Beijing That's a fact; It's a thing we can't deny. I wonder if that's true ...`,
      `I'm really excited that you're trying out ntfy. Did you know that there are a few public topics, such as ntfy.sh/stats and ntfy.sh/announcements.`,
      `It's interesting to hear what people use ntfy for. I've heard people talk about using it for so many cool things. What do you use it for?`,
    ])[0];
    try {
      await api.publishText(topic, title, message, tags);
    } catch (e) {
      console.log(`[SubscriptionPopup] Error publishing message`, e);
      toast.error(t("message_bar_error_publishing"));
    }
  };

  const handleClearAll = async () => {
    console.log(`[SubscriptionPopup] Deleting all notifications from ${props.subscription.id}`);
    await subscriptionManager.deleteNotifications(props.subscription.id);
  };

  const handleSetMutedUntil = async (mutedUntil: number) => {
    await subscriptionManager.setMutedUntil(subscription.id, mutedUntil);
  };

  const handleUnsubscribe = async () => {
    console.log(`[SubscriptionPopup] Unsubscribing from ${props.subscription.id}`, props.subscription);
    await subscriptionManager.remove(props.subscription);
    if (session.exists() && !subscription.internal) {
      try {
        await accountApi.deleteSubscription(props.subscription.baseUrl, props.subscription.topic);
      } catch (e) {
        console.log(`[SubscriptionPopup] Error unsubscribing`, e);
        if (e instanceof UnauthorizedError) {
          await session.resetAndRedirect(routes.login);
        }
      }
    }
    const newSelected = await subscriptionManager.first(); // May be undefined
    if (newSelected && !newSelected.internal) {
      navigate(routes.forSubscription(newSelected), { replace: true }); // 지운 구독 화면으로 돌아오지 않게
    } else {
      navigate(routes.app, { replace: true });
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{props.trigger}</DropdownMenuTrigger>
        <DropdownMenuContent align={placement === "right" ? "end" : "start"} className="min-w-56">
          <DropdownMenuItem onSelect={handleChangeDisplayName}>
            <Pencil />
            {t("action_bar_change_display_name")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => iconInputRef.current?.click()}>
            <ImagePlus />
            아이콘 바꾸기
          </DropdownMenuItem>
          {subscription.icon && (
            <DropdownMenuItem onSelect={handleRemoveIcon}>
              <ImageMinus />
              아이콘 지우기
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={handleSendTestMessage}>
            <Send />
            {t("action_bar_send_test_notification")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleClearAll}>
            <ListX />
            {t("action_bar_clear_notifications")}
          </DropdownMenuItem>
          {!!subscription.mutedUntil && (
            <DropdownMenuItem onSelect={() => handleSetMutedUntil(0)}>
              <Bell />
              {t("action_bar_unmute_notifications")}
            </DropdownMenuItem>
          )}
          {!subscription.mutedUntil && (
            <DropdownMenuItem onSelect={() => handleSetMutedUntil(1)}>
              <BellOff />
              {t("action_bar_mute_notifications")}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={handleUnsubscribe}>
            <CircleMinus />
            {t("action_bar_unsubscribe")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <input ref={iconInputRef} type="file" accept="image/*" hidden onChange={handleIconFile} />
      {/* Radix Dialog 는 스스로 포털을 사용하므로 MUI 의 <Portal> 래퍼는 필요 없다. */}
      <DisplayNameDialog open={displayNameDialogOpen} subscription={subscription} onClose={() => setDisplayNameDialogOpen(false)} />
    </>
  );
};

interface DisplayNameDialogProps {
  open: boolean;
  subscription: Subscription;
  onClose: () => void;
}

const DisplayNameDialog = (props: DisplayNameDialogProps) => {
  const { t } = useTranslation();
  const { subscription } = props;
  const [error, setError] = useState("");
  const [displayName, setDisplayName] = useState(subscription.displayName ?? "");

  const handleSave = async () => {
    await subscriptionManager.setDisplayName(subscription.id, displayName);
    if (session.exists() && !subscription.internal) {
      try {
        console.log(`[SubscriptionSettingsDialog] Updating subscription display name to ${displayName}`);
        await accountApi.updateSubscription(subscription.baseUrl, subscription.topic, { display_name: displayName });
      } catch (e) {
        console.log(`[SubscriptionSettingsDialog] Error updating subscription`, e);
        if (e instanceof UnauthorizedError) {
          await session.resetAndRedirect(routes.login);
        } else {
          setError((e as Error).message);
          return;
        }
      }
    }
    props.onClose();
  };

  return (
    <Dialog open={props.open} onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("display_name_dialog_title")}</DialogTitle>
          <DialogDescription>{t("display_name_dialog_description")}</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Input
            autoFocus
            placeholder={t("display_name_dialog_placeholder")}
            value={displayName}
            onChange={(ev) => setDisplayName(ev.target.value)}
            type="text"
            maxLength={64}
            aria-label={t("display_name_dialog_placeholder")}
            className="pr-10"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute top-1/2 right-1 size-7 -translate-y-1/2"
            onClick={() => setDisplayName("")}
          >
            <X />
          </Button>
        </div>
        <DialogFooter status={error}>
          <Button variant="ghost" onClick={props.onClose}>
            {t("common_cancel")}
          </Button>
          <Button variant="ghost" onClick={handleSave}>
            {t("common_save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
